import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HttpError, request } from "../../api/http";
import { useAuth } from "../../context/AuthContext";
import { useMarket } from "../../context/MarketContext";
import {
  AllLeadsResponse,
  AllLeadsRow,
  SEARCH_STAGE_LABELS,
  SEARCH_STAGES,
  SearchStage,
} from "../../types/allLeads";
import Pagination from "../OnboardingCustomers/components/Pagination";
import AllLeadsActions from "./components/AllLeadsActions";

const DEFAULT_PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

const fmtDate = (iso: string | null): string => {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const fmtCapital = (n: number | null): string => {
  if (n === null) return "—";
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(n);
};

export default function AllLeads() {
  const { token } = useAuth();
  const { markets } = useMarket();

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [stage, setStage] = useState<SearchStage | "">("");

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [data, setData] = useState<AllLeadsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    if (debouncedSearch) params.set("q", debouncedSearch);
    if (stage) params.set("stage", stage);
    params.set("limit", String(pageSize));
    params.set("offset", String(page * pageSize));
    try {
      const response = await request<AllLeadsResponse>(
        `/api/admin/operations/leads/search?${params.toString()}`,
        { token, markets },
      );
      setData(response);
    } catch (err) {
      setError(
        err instanceof HttpError
          ? `HTTP ${err.status}`
          : "No se pudo cargar la búsqueda",
      );
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, stage, page, pageSize, token, markets]);

  useEffect(() => {
    load();
  }, [load]);

  const clearFilters = () => {
    setSearch("");
    setStage("");
    setPage(0);
  };

  return (
    <div style={{ padding: "1rem 1.5rem", maxWidth: 1600, margin: "0 auto" }}>
      <div style={{ marginBottom: "0.75rem" }}>
        <h2 style={{ margin: 0, fontSize: "1.15rem", color: "#2A3F54" }}>
          Búsqueda global de leads
        </h2>
        <p style={{ margin: "0.25rem 0 0", color: "#73879C", fontSize: ".85rem" }}>
          Busca por email, teléfono, nombre, DNI, número de póliza, lead id o ULID.
          Filtra por etapa para acotar el listado.
        </p>
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "0.5rem",
          background: "white",
          padding: "0.7rem 0.95rem",
          borderRadius: "0.55rem",
          boxShadow: "0 1px 3px rgba(0,0,0,.04)",
          marginBottom: "0.65rem",
        }}
      >
        <input
          type="search"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          placeholder="Buscar por email, teléfono, nombre, DNI, póliza, lead id, ULID…"
          style={{ ...inputStyle, minWidth: 320, flex: "1 1 320px" }}
        />
        <select
          value={stage}
          onChange={(e) => {
            setStage((e.target.value as SearchStage | "") || "");
            setPage(0);
          }}
          style={inputStyle}
        >
          <option value="">Todas las etapas</option>
          {SEARCH_STAGES.map((s) => (
            <option key={s} value={s}>
              {SEARCH_STAGE_LABELS[s]}
            </option>
          ))}
        </select>
        <button type="button" onClick={clearFilters} style={ghostBtn}>
          Limpiar
        </button>
        <div style={{ marginLeft: "auto", fontSize: ".8rem", color: "#73879C" }}>
          {data ? `${data.results.length} de ${data.count}` : "—"}
        </div>
      </div>

      {error && (
        <div
          style={{
            padding: "0.6rem 0.85rem",
            background: "#fde2e2",
            color: "#b32424",
            borderRadius: "0.45rem",
            marginBottom: "0.65rem",
            fontSize: ".82rem",
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          background: "white",
          borderRadius: "0.6rem",
          boxShadow: "0 1px 3px rgba(0,0,0,.04)",
          overflow: "hidden",
          opacity: loading ? 0.6 : 1,
          transition: "opacity .15s ease",
        }}
      >
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: ".85rem",
            }}
          >
            <thead>
              <tr style={{ background: "#f8f9fb" }}>
                <Th>Lead</Th>
                <Th>Broker</Th>
                <Th>Email</Th>
                <Th>Nombre</Th>
                <Th>DNI/NIE</Th>
                <Th>Status</Th>
                <Th>Market</Th>
                <Th>Carrier</Th>
                <Th style={{ textAlign: "right" }}>Capital</Th>
                <Th>Coverage</Th>
                <Th>Suscripción</Th>
                <Th>Creado</Th>
                <Th style={{ textAlign: "right" }}>Acciones</Th>
              </tr>
            </thead>
            <tbody>
              {data === null || data.results.length === 0 ? (
                <tr>
                  <td
                    colSpan={13}
                    style={{
                      padding: "1rem",
                      color: "#73879C",
                      textAlign: "center",
                    }}
                  >
                    {loading ? "Cargando…" : "Sin resultados"}
                  </td>
                </tr>
              ) : (
                data.results.map((row) => <Row key={row.id} row={row} />)
              )}
            </tbody>
          </table>
        </div>
        {data && data.count > 0 && (
          <Pagination
            page={page}
            pageSize={pageSize}
            total={data.count}
            onPageChange={(p) => setPage(p)}
            onPageSizeChange={(s) => {
              setPageSize(s);
              setPage(0);
            }}
            disabled={loading}
          />
        )}
      </div>
    </div>
  );
}

function Row({ row }: { row: AllLeadsRow }) {
  return (
    <tr style={{ borderBottom: "1px solid #f1f3f5", background: "white" }}>
      <Td mono>
        {row.ulid ? (
          <Link
            to={`/leads/${row.ulid}`}
            style={{ color: "#0069d9", textDecoration: "none", fontWeight: 600 }}
          >
            #{row.id}
          </Link>
        ) : (
          <span>#{row.id}</span>
        )}
        <div style={{ fontSize: ".65rem", color: "#73879C" }} title={row.ulid}>
          {row.ulid ? row.ulid.slice(-10) : ""}
        </div>
      </Td>
      <Td>{row.brokerName ?? "—"}</Td>
      <Td>{row.email ?? "—"}</Td>
      <Td>{row.fullname || "—"}</Td>
      <Td mono>{row.idNumber || "—"}</Td>
      <Td>{row.status ?? "—"}</Td>
      <Td mono>{row.market ?? "—"}</Td>
      <Td>{row.carrier ?? "—"}</Td>
      <Td mono style={{ textAlign: "right" }}>
        {fmtCapital(row.capital)}
      </Td>
      <Td>{row.coverage ?? "—"}</Td>
      <Td>
        {row.subscriptionId ? (
          <Link
            to={`/subscriptions/${row.subscriptionId}`}
            style={{
              color: "#0069d9",
              textDecoration: "none",
              fontFamily: "monospace",
              fontSize: ".78rem",
            }}
          >
            {row.subscription_status ?? "—"}
          </Link>
        ) : (
          row.subscription_status ?? "—"
        )}
      </Td>
      <Td>{fmtDate(row.date)}</Td>
      <Td style={{ textAlign: "right" }}>
        <AllLeadsActions row={row} />
      </Td>
    </tr>
  );
}

function Th({
  children,
  style,
}: {
  children?: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <th
      style={{
        padding: "0.55rem 0.75rem",
        textAlign: "left",
        fontSize: ".7rem",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: ".4px",
        color: "#73879C",
        borderBottom: "1px solid #e9ecef",
        ...style,
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  mono,
  style,
}: {
  children: React.ReactNode;
  mono?: boolean;
  style?: React.CSSProperties;
}) {
  return (
    <td
      style={{
        padding: "0.55rem 0.75rem",
        fontFamily: mono ? "monospace" : "inherit",
        color: "#1f2933",
        verticalAlign: "middle",
        ...style,
      }}
    >
      {children}
    </td>
  );
}

const inputStyle: React.CSSProperties = {
  padding: "0.4rem 0.55rem",
  borderRadius: "0.4rem",
  border: "1px solid #dee2e6",
  background: "#f8f9fb",
  fontSize: ".85rem",
};

const ghostBtn: React.CSSProperties = {
  padding: "0.4rem 0.85rem",
  borderRadius: "0.4rem",
  background: "white",
  color: "#73879C",
  border: "1px solid #dee2e6",
  fontSize: ".78rem",
  fontWeight: 600,
  cursor: "pointer",
};
